/* ==========================================================================
   X-HEALTHY — global.js
   Shared helpers loaded on every page. Component-specific scripts live in
   their own files and are loaded (defer) only by the sections that need them.
   ========================================================================== */

(() => {
  const theme = (window.theme = window.theme || {});

  /* ---------- Event bus ---------- */
  const bus = new EventTarget();
  theme.events = {
    on(name, handler) {
      bus.addEventListener(name, handler);
      return () => bus.removeEventListener(name, handler);
    },
    emit(name, detail) {
      bus.dispatchEvent(new CustomEvent(name, { detail }));
    },
  };

  /* ---------- Helpers ---------- */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';

  let trapCleanup = null;
  let scrollLocks = 0;

  theme.utils = {
    debounce(fn, wait = 300) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, wait = 100) {
      let last = 0;
      return (...args) => {
        const now = Date.now();
        if (now - last >= wait) {
          last = now;
          fn(...args);
        }
      };
    },

    fetchConfig(type = 'json', body) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
        body: body ? JSON.stringify(body) : undefined,
      };
    },

    /** Fetch a section (or page) via the Section Rendering API and return a parsed Document. */
    async fetchSection(url, sectionId) {
      const target = new URL(url, window.location.origin);
      if (sectionId) target.searchParams.set('section_id', sectionId);
      const res = await fetch(target.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    },

    /** Format cents using the shop money format. Always Western digits. */
    formatMoney(cents, format = theme.moneyFormat || '{{amount}}') {
      const value = Number(cents) / 100;
      const fmt = (n, decimals, thousands = ',', decimal = '.') => {
        const [int, frac] = n.toFixed(decimals).split('.');
        return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
      };
      return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return fmt(value, 0);
          case 'amount_with_comma_separator':
            return fmt(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return fmt(value, 0, '.');
          default:
            return fmt(value, 2);
        }
      });
    },

    /** Announce a message to screen readers via the global live region. */
    announce(message) {
      const region = document.getElementById('LiveRegion');
      if (!region) return;
      region.textContent = '';
      requestAnimationFrame(() => (region.textContent = message));
    },

    trapFocus(container, initial) {
      theme.utils.releaseFocus();
      const focusables = () =>
        [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const items = focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', onKeydown);
      trapCleanup = () => document.removeEventListener('keydown', onKeydown);
      (initial || focusables()[0] || container).focus({ preventScroll: true });
    },

    releaseFocus(returnTo) {
      if (trapCleanup) trapCleanup();
      trapCleanup = null;
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
    },

    lockScroll() {
      scrollLocks += 1;
      document.body.classList.add('scroll-locked');
    },

    unlockScroll() {
      scrollLocks = Math.max(0, scrollLocks - 1);
      if (!scrollLocks) document.body.classList.remove('scroll-locked');
    },

    isRTL() {
      return document.documentElement.dir === 'rtl';
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
  };

  /* ---------- Toast ---------- */
  const ICON = (name) =>
    `<svg class="icon" aria-hidden="true" focusable="false"><use href="#icon-${name}"></use></svg>`;

  theme.toast = ({ message, type = 'success', action, duration = 4000 } = {}) => {
    let region = document.querySelector('.toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.innerHTML = `
      <span class="toast__icon">${ICON(type === 'error' ? 'alert' : 'check')}</span>
      <span class="toast__message"></span>
      ${action ? `<a class="toast__action" href="${action.url}">${action.label}</a>` : ''}
      <button type="button" class="toast__close" aria-label="${theme.strings?.close || 'Close'}">${ICON('close')}</button>`;
    toast.querySelector('.toast__message').textContent = message;

    const dismiss = () => {
      if (toast.classList.contains('is-leaving')) return;
      toast.classList.add('is-leaving');
      setTimeout(() => toast.remove(), 260);
    };
    toast.querySelector('.toast__close').addEventListener('click', dismiss);
    let timer = setTimeout(dismiss, duration);
    toast.addEventListener('mouseenter', () => clearTimeout(timer));
    toast.addEventListener('mouseleave', () => (timer = setTimeout(dismiss, 1500)));

    region.appendChild(toast);
    return dismiss;
  };

  /* ---------- Cart helpers ---------- */
  theme.cart = {
    async add(formData) {
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      const cart = await theme.cart.get();
      theme.events.emit('cart:updated', { cart, item: data, source: 'add' });
      return { item: data, cart };
    },
    async get() {
      const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },
  };

  // Keep every cart counter in sync
  theme.events.on('cart:updated', ({ detail }) => {
    if (!detail?.cart) return;
    const count = detail.cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = theme.utils.formatMoney(detail.cart.total_price);
    });
  });

  /* ---------- Wishlist store (browser only, no login) ---------- */
  const WISHLIST_KEY = 'xh-wishlist';
  const readList = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch {
      return [];
    }
  };
  const writeList = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {
      /* storage unavailable (private mode) */
    }
  };

  theme.wishlist = {
    all: () => readList(WISHLIST_KEY),
    has: (handle) => readList(WISHLIST_KEY).includes(handle),
    toggle(handle) {
      const list = readList(WISHLIST_KEY);
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(handle);
      writeList(WISHLIST_KEY, list.slice(0, 100));
      const added = index === -1;
      theme.events.emit('wishlist:updated', { handle, added, list });
      return added;
    },
  };

  const syncWishlistCount = () => {
    const count = theme.wishlist.all().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };
  theme.events.on('wishlist:updated', syncWishlistCount);
  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) {
      syncWishlistCount();
      theme.events.emit('wishlist:updated', { list: theme.wishlist.all() });
    }
  });

  /* ---------- <quantity-input> ---------- */
  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input');
          this.buttons = this.querySelectorAll('button');
          this.buttons.forEach((button) => button.addEventListener('click', this.onButton.bind(this)));
          this.input.addEventListener('change', () => this.validate());
          this.validate(false);
        }

        onButton(event) {
          const before = this.input.value;
          if (event.currentTarget.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (before !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        validate() {
          const value = Number(this.input.value);
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          if (Number.isNaN(value) || value < min) this.input.value = min;
          if (value > max) this.input.value = max;
          this.querySelector('[name="minus"]').disabled = Number(this.input.value) <= min;
          this.querySelector('[name="plus"]').disabled = Number(this.input.value) >= max;
        }
      }
    );
  }

  /* ---------- <read-more>: clamp long text to N lines with a toggle ---------- */
  if (!customElements.get('read-more')) {
    customElements.define(
      'read-more',
      class ReadMore extends HTMLElement {
        connectedCallback() {
          this.content = this.querySelector('.read-more__content');
          this.button = this.querySelector('.read-more__toggle');
          if (!this.content || !this.button) return;
          this.style.setProperty('--lines', this.dataset.lines || 3);
          requestAnimationFrame(() => {
            if (this.content.scrollHeight > this.content.clientHeight + 4) this.button.hidden = false;
          });
          this.button.addEventListener('click', () => {
            const open = this.toggleAttribute('open');
            this.button.setAttribute('aria-expanded', String(open));
            this.button.querySelector('[data-more]').hidden = open;
            this.button.querySelector('[data-less]').hidden = !open;
          });
        }
      }
    );
  }

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
        )
      : null;

  theme.initReveal = (root = document) => {
    const items = root.querySelectorAll('.reveal:not(.is-revealed)');
    if (!revealObserver || !document.body.classList.contains('has-reveal')) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Theme editor ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.documentElement.classList.add('shopify-design-mode');
  }

  document.addEventListener('shopify:section:load', (event) => theme.initReveal(event.target));

  const init = () => {
    theme.initReveal();
    syncWishlistCount();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
